import React, { useCallback, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useFormContext } from 'react-hook-form'
import { Button } from '@refera/ui-web'
import { useDialog } from '_/hooks/use-dialog'
import { navigate, useParams } from '@reach/router'
import useRolePermission from '_/hooks/use-role-permission'
import {
  createInspectionBudget,
  updateInspectionBudget,
  duplicateInspectionBudget,
} from '_/modules/inspectionBudget/actions'
import { useToast } from '_/hooks/use-toast'
import { useInspectionLoading } from '_/views/inspection-budget/hooks/use-inspection-loading'
import { normalizePayload, BUDGET_URL } from '_/views/inspection-budget/utils/constants'
import { DuplicateBudgetModal } from '_/views/inspection-budget/components/modals/duplicate-budget-modal'
import { MemoryCalculationModal } from '_/views/inspection-budget/components/modals/memory-calculation-modal'

export function SaveButton() {
  const dispatch = useDispatch()
  const { budgetId } = useParams()
  const { isAdmin } = useRolePermission()
  const { showToast } = useToast()
  const {
    handleSubmit,
    reset,
    formState: { dirtyFields },
  } = useFormContext()
  const { budget, loading, setLoading, setIsEditing, setInitialFormValues, budgetType } =
    useInspectionLoading()

  const getFirstErrorPath = (errors, parentKey = '') => {
    if (!errors) return null

    for (const key of Object.keys(errors)) {
      const value = errors[key]
      const currentPath = parentKey ? `${parentKey}.${key}` : key

      if (value?.message) return currentPath

      if (Array.isArray(value)) {
        for (const [index, item] of value.entries()) {
          const nested = getFirstErrorPath(item, `${currentPath}.${index}`)
          if (nested) return nested
        }
      }

      if (typeof value === 'object' && value !== null) {
        const nested = getFirstErrorPath(value, currentPath)
        if (nested) return nested
      }
    }

    return null
  }

  const focusFieldByPath = path => {
    const el = document.querySelector(`[data-field-path="${path}"]`)
    if (!el) return false

    el.scrollIntoView({ behavior: 'smooth', block: 'center' })
    el.focus()
    el.select?.()
    return true
  }

  function commitActiveFieldBeforeSubmit() {
    if (typeof document === 'undefined') return

    const { activeElement } = document
    activeElement?.blur?.()
  }

  const expandCollapsedSections = path => {
    if (!path) return

    const el = document.querySelector(`[data-field-path="${path}"]`)
    if (!el) return

    const accordions = []
    const collapses = []
    let current = el.parentElement

    while (current) {
      if (current.classList.contains('MuiAccordion-root')) {
        accordions.push(current)
      }

      if (current.classList.contains('MuiCollapse-root')) {
        collapses.push(current)
      }

      current = current.parentElement
    }

    // Opens parent accordions from outermost to innermost
    accordions.reverse().forEach(accordion => {
      const summary = accordion.querySelector('.MuiAccordionSummary-root')
      const isExpanded = summary?.getAttribute('aria-expanded') === 'true'

      if (!isExpanded) {
        summary?.click()
      }
    })

    // Opens collapsed sections that are ancestors of the errored field
    collapses.reverse().forEach(collapse => {
      if (!collapse.classList.contains('MuiCollapse-hidden')) return

      let sibling = collapse.previousElementSibling
      let button = null

      while (sibling && !button) {
        button = sibling
          .querySelector(
            'button svg[data-testid="KeyboardArrowDownIcon"], button svg[data-testid="KeyboardArrowUpIcon"]'
          )
          ?.closest('button')
        sibling = sibling.previousElementSibling
      }

      button?.click()
    })
  }

  function handleValidationError(errors) {
    const firstErrorPath = getFirstErrorPath(errors)
    expandCollapsedSections(firstErrorPath)

    requestAnimationFrame(() => requestAnimationFrame(() => focusFieldByPath(firstErrorPath)))

    showToast({
      type: 'error',
      message: 'Um ou mais dados do formulário possuem problemas.',
    })
  }

  const handleSuccess = useCallback(
    res => {
      if (budgetId) {
        const formValues = structuredClone(res)
        const initialSnapshot = structuredClone(res)
        setInitialFormValues(initialSnapshot)
        reset(formValues)
        setIsEditing(false)
      } else {
        navigate(`${BUDGET_URL[budgetType]}/${res?.id}`)
      }

      showToast({ type: 'success' })
    },
    [budgetId, reset, showToast, setIsEditing, setInitialFormValues]
  )
  const handleSave = useCallback(
    data => {
      if (!isAdmin) {
        showToast({ message: 'Você não tem permissão para realizar esta operação.', type: 'error' })
        return
      }

      const hasDirtyResolution = value => {
        if (value === true) return true
        if (Array.isArray(value)) return value.some(hasDirtyResolution)
        if (typeof value === 'object' && value !== null) {
          return Object.values(value).some(hasDirtyResolution)
        }
        return false
      }

      const calcMemoryChangedKeys = (dirtyFields?.inspectionBudgetCompartments || []).flatMap(
        (compartmentDirty, compartmentIndex) => {
          const resolutionsDirty = compartmentDirty?.inspectionBudgetResolutions || []
          return resolutionsDirty
            .map((resolutionDirty, resolutionIndex) =>
              hasDirtyResolution(resolutionDirty)
                ? `${compartmentIndex + 1}:${resolutionIndex + 1}`
                : null
            )
            .filter(Boolean)
        }
      )

      const payload = normalizePayload({
        ...data,
        budgetType,
        calcMemoryChangedKeys,
      })

      const request = budgetId
        ? updateInspectionBudget(budgetId, payload)
        : createInspectionBudget(payload)

      setLoading(true)
      dispatch(request)
        .then(res => {
          handleSuccess(res)
        })
        .catch(err => {
          const errorMessage = err?.errorMessage ?? 'Ocorreu um erro ao realizar a operação.'
          showToast({ type: 'error', message: errorMessage })
        })
        .finally(() => {
          setLoading(false)
        })
    },
    [
      dispatch,
      budgetId,
      reset,
      isAdmin,
      showToast,
      setLoading,
      handleSuccess,
      dirtyFields,
      budgetType,
    ]
  )

  return (
    <Button
      color="primary"
      variant="primary"
      onClick={event => {
        commitActiveFieldBeforeSubmit()
        handleSubmit(handleSave, handleValidationError)(event)
      }}
      disabled={budget?.isOriginal || loading}
    >
      Salvar
    </Button>
  )
}

export function CancelButton() {
  const {
    formState: { isDirty },
    reset,
    setValue,
  } = useFormContext()
  const { budgetId } = useParams()
  const { setIsEditing, initialFormValues } = useInspectionLoading()

  const { closeDialog, showDialog } = useDialog()
  function handleCancel() {
    const restoreSnapshot = () => {
      const snapshot = initialFormValues ? structuredClone(initialFormValues) : undefined
      reset(snapshot)
      Promise.resolve().then(() => {
        const compartments = snapshot?.inspectionBudgetCompartments || []
        compartments.forEach((compartment, compartmentIndex) => {
          const resolutions = compartment?.inspectionBudgetResolutions || []

          resolutions.forEach((resolution, resolutionIndex) => {
            setValue(
              `inspectionBudgetCompartments.${compartmentIndex}.inspectionBudgetResolutions.${resolutionIndex}.inspectionDescription`,
              resolution?.inspectionDescription ?? '',
              { shouldDirty: false }
            )
            setValue(
              `inspectionBudgetCompartments.${compartmentIndex}.inspectionBudgetResolutions.${resolutionIndex}.serviceDescription`,
              resolution?.serviceDescription ?? '',
              { shouldDirty: false }
            )
            setValue(
              `inspectionBudgetCompartments.${compartmentIndex}.inspectionBudgetResolutions.${resolutionIndex}.laborTime`,
              resolution?.laborTime ?? 0,
              { shouldDirty: false }
            )
            setValue(
              `inspectionBudgetCompartments.${compartmentIndex}.inspectionBudgetResolutions.${resolutionIndex}.laborValue`,
              resolution?.laborValue ?? 0,
              { shouldDirty: false }
            )
            setValue(
              `inspectionBudgetCompartments.${compartmentIndex}.inspectionBudgetResolutions.${resolutionIndex}.laborValueMax`,
              resolution?.laborValueMax ?? 0,
              { shouldDirty: false }
            )
            setValue(
              `inspectionBudgetCompartments.${compartmentIndex}.inspectionBudgetResolutions.${resolutionIndex}.agencyValue`,
              resolution?.agencyValue ?? 0,
              { shouldDirty: false }
            )

            const materials = resolution?.budgetResolutionMaterials || []
            materials.forEach((material, materialIndex) => {
              const basePath = `inspectionBudgetCompartments.${compartmentIndex}.inspectionBudgetResolutions.${resolutionIndex}.budgetResolutionMaterials.${materialIndex}`
              setValue(`${basePath}.description`, material?.description ?? '', {
                shouldDirty: false,
              })
              setValue(`${basePath}.quantity`, material?.quantity ?? 0, {
                shouldDirty: false,
              })
              setValue(`${basePath}.unitPrice`, material?.unitPrice ?? 0, {
                shouldDirty: false,
              })
              setValue(`${basePath}.total`, material?.total ?? 0, {
                shouldDirty: false,
              })
            })
          })
        })
        setIsEditing(false)
      })
    }

    if (isDirty) {
      showDialog({
        type: 'warning',
        subject: 'Cancelar',
        description: 'Você tem certeza que deseja executar esta ação?',
        onApprove: () => {
          closeDialog()
          if (budgetId) {
            restoreSnapshot()
          } else {
            navigate(-1)
          }
        },
        onCancel: closeDialog,
        labelApprove: 'Confirmar',
        labelCancel: 'Cancelar',
      })
    } else if (budgetId) {
      restoreSnapshot()
    } else {
      navigate(-1)
    }
  }
  return (
    <Button onClick={handleCancel} color="red" variant="secondary">
      Cancelar
    </Button>
  )
}

export function DuplicateBudget() {
  const { budgetId } = useParams()
  const { budget, loading, setLoading, setRelatedBudgets, isEditing } = useInspectionLoading()
  const [openModal, setOpenModal] = useState(false)
  const [openMemoryModal, setOpenMemoryModal] = useState(false)
  const { closeDialog, showDialog } = useDialog()
  const { showToast } = useToast()
  const dispatch = useDispatch()
  const duplicateFrom = budget?.duplicateFrom
  const isOriginal = budget?.isOriginal

  const handleDuplicateOriginalBudget = useCallback(() => {
    setLoading(true)
    dispatch(duplicateInspectionBudget(budget?.id, { isOriginal }))
      .then(res => {
        const duplicatedList = res?.data ?? []

        setRelatedBudgets(prev => [...duplicatedList, ...(prev ?? [])])
        showToast({ type: 'success' })
      })
      .catch(e =>
        showToast({
          type: 'error',
          message: e?.error_message ?? 'Ocorreu um erro ao realizar a operação.',
        })
      )
      .finally(() => {
        setLoading(false)
      })
  }, [dispatch, budget?.id, isOriginal, setLoading, setRelatedBudgets, showToast])

  const handleDuplicate = useCallback(() => {
    if (isOriginal) {
      return showDialog({
        type: 'warning',
        subject: 'Você tem certeza que deseja executar esta ação?',
        onApprove: () => {
          closeDialog()
          handleDuplicateOriginalBudget()
        },
        onCancel: closeDialog,
        labelApprove: 'Confirmar',
        labelCancel: 'Cancelar',
      })
    }

    if (duplicateFrom) {
      return showToast({
        type: 'error',
        message:
          'Você só pode duplicar orçamentos principais e originais. Encontre o principal deste orçamento, acessando a seção de orçamentos relacionados.',
      })
    }
    return setOpenModal(true)
  }, [duplicateFrom, showToast, isOriginal, closeDialog, handleDuplicateOriginalBudget])

  return (
    <>
      <Button
        color="primary"
        variant="secondary"
        disabled={!budgetId || loading}
        onClick={handleDuplicate}
      >
        Duplicar Orçamento
      </Button>
      <Button
        color="primary"
        variant="secondary"
        disabled={!budgetId || loading || isEditing}
        onClick={() => setOpenMemoryModal(true)}
      >
        Memória de cálculo
      </Button>
      <DuplicateBudgetModal openModal={openModal} setOpenModal={setOpenModal} />
      <MemoryCalculationModal openModal={openMemoryModal} setOpenModal={setOpenMemoryModal} />
    </>
  )
}

export function EditButton() {
  const { budgetId } = useParams()
  const { setIsEditing, loading, budget } = useInspectionLoading()

  if (!budgetId) return null

  return (
    <Button
      color="primary"
      variant="secondary"
      onClick={() => setIsEditing(true)}
      disabled={loading || budget?.isOriginal}
    >
      Editar
    </Button>
  )
}
